import React, { useState, useEffect } from 'react';
import {
  Pane,
  Button,
  Dialog,
  TextInputField,
  SelectField,
  Table,
  Badge,
  IconButton,
  toaster,
  Spinner,
  Menu,
  Popover,
  Position,
  CaretDownIcon,
  CrossIcon,
  TrashIcon,
  RefreshIcon,
  Text,
  Tooltip,
  Card,
  Heading,
  Textarea,
  ArrowUpIcon,
  ArrowDownIcon,
  PlusIcon,
  EditIcon,
  Checkbox
} from 'evergreen-ui';
import { useProject } from '../context/ProjectContext';
import api from '../services/api';

const TASK_TEMPLATES = {
  NMAP_SCAN: {
    name: 'Nmap Scan',
    category: 'Scanning',
    templates: {
      INITIAL_SCAN: {
        name: 'Initial Scan (Top 10k ports)',
        description: 'Quick scan of top 10,000 ports with service detection',
        parameters: {
          command: 'nmap --top-ports 10000 -Pn -n -vv --open -sV -T4',
          scan_type: 'initial',
          intensity: 'medium'
        }
      },
      FULL_SCAN: {
        name: 'Full Port Scan',
        description: 'Comprehensive scan of all 65,535 ports',
        parameters: {
          command: 'nmap -p- -Pn -n -vv --open -T2',
          scan_type: 'full',
          intensity: 'low'
        }
      },
      RCE_PORTS: {
        name: 'RCE Ports Scan',
        description: 'Scan for common RCE ports including 4444-4445, 5555-5556, 7000-7004, 8000-8003, 9000-9003, and high-value ports',
        parameters: {
          command: 'nmap -p 1090,1098,1099,4444-4445,5555-5556,7000-7004,8000-8003,9000-9003,9503,11099,11111,45000-45001,47001-47002,4848,50500,8686,9012 -Pn -n -vv --open -sV -T4',
          scan_type: 'rce',
          intensity: 'high'
        }
      },
      CUSTOM: {
        name: 'Custom Nmap Scan',
        description: 'Define your own nmap parameters',
        parameters: {
          command: '',
          scan_type: 'custom',
          intensity: 'medium'
        }
      }
    }
  },
  HTTPX_SCAN: {
    name: 'HTTPX Scan',
    category: 'Scanning',
    templates: {
      QUICK_HTTP: {
        name: 'Quick HTTP Discovery',
        description: 'Fast HTTP probing with common ports',
        parameters: {
          command: 'httpx -l {target} -timeout 10 -threads 50',
          scan_type: 'quick',
          intensity: 'medium'
        }
      },
      FULL_HTTP: {
        name: 'Full HTTP Discovery',
        description: 'Comprehensive HTTP probing with all ports',
        parameters: {
          command: 'httpx -l {target} -timeout 15 -threads 30 -ports 80,443,8080,8443,3000,5000,8000,8888',
          scan_type: 'full',
          intensity: 'low'
        }
      },
      SCREENSHOT: {
        name: 'HTTPX with Screenshots',
        description: 'HTTP probing with screenshot capture using -ss option',
        parameters: {
          command: 'httpx -l {target} -ss -timeout 20 -threads 25 -screenshot-dir screenshots',
          scan_type: 'screenshot',
          intensity: 'medium',
          screenshot: true,
          screenshot_dir: 'screenshots'
        }
      },
      CUSTOM: {
        name: 'Custom HTTPX Scan',
        description: 'Define your own httpx parameters',
        parameters: {
          command: '',
          scan_type: 'custom',
          intensity: 'medium'
        }
      }
    }
  },
  NUCLEI_SCAN: {
    name: 'Nuclei Scan',
    category: 'Scanning',
    templates: {
      QUICK_SCAN: {
        name: 'Quick Vulnerability Scan',
        description: 'Fast scan with common vulnerability templates',
        parameters: {
          command: 'nuclei -t cve,default-logins,exposures -severity critical,high -rate-limit 150',
          scan_type: 'quick',
          intensity: 'medium'
        }
      },
      FULL_SCAN: {
        name: 'Comprehensive Vulnerability Scan',
        description: 'Full scan with all available templates',
        parameters: {
          command: 'nuclei -t all -severity critical,high,medium -rate-limit 100',
          scan_type: 'full',
          intensity: 'low'
        }
      },
      CUSTOM: {
        name: 'Custom Nuclei Scan',
        description: 'Define your own nuclei parameters',
        parameters: {
          command: '',
          scan_type: 'custom',
          intensity: 'medium'
        }
      }
    }
  },
  SUBFINDER_SCAN: {
    name: 'Subfinder Scan',
    category: 'Reconnaissance',
    templates: {
      QUICK_SUBDOMAINS: {
        name: 'Quick Subdomain Enumeration',
        description: 'Fast subdomain discovery with common sources',
        parameters: {
          command: 'subfinder -d {target} -silent',
          scan_type: 'quick',
          intensity: 'medium'
        }
      },
      COMPREHENSIVE: {
        name: 'Comprehensive Subdomain Enumeration',
        description: 'Thorough subdomain discovery with all sources',
        parameters: {
          command: 'subfinder -d {target} -all -silent',
          scan_type: 'comprehensive',
          intensity: 'low'
        }
      },
      CUSTOM: {
        name: 'Custom Subfinder Scan',
        description: 'Define your own subfinder parameters',
        parameters: {
          command: '',
          scan_type: 'custom',
          intensity: 'medium'
        }
      }
    }
  },
  FFUF: {
    name: 'FFUF',
    category: 'Scanning',
    templates: {
      QUICK_FUZZ: {
        name: 'Quick Directory Fuzz',
        description: 'Directory fuzzing with common wordlist and auto-calibration',
        parameters: {
          scan_type: 'quick',
          intensity: 'medium'
        }
      },
      CUSTOM: {
        name: 'Custom FFUF Scan',
        description: 'Define your own ffuf parameters',
        parameters: {
          scan_type: 'custom',
          intensity: 'medium'
        }
      }
    }
  },
  KATANA_SCAN: {
    name: 'Katana Crawl',
    category: 'Reconnaissance',
    templates: {
      QUICK_CRAWL: {
        name: 'Quick Web Crawl',
        description: 'Crawl target with depth 3, silent mode',
        parameters: {
          depth: 3,
          scan_type: 'quick',
          intensity: 'medium'
        }
      },
      DEEP_CRAWL: {
        name: 'Deep Crawl with JS',
        description: 'Deep crawl (depth 5) with JavaScript parsing enabled',
        parameters: {
          depth: 5,
          js_crawl: true,
          scan_type: 'deep',
          intensity: 'high'
        }
      },
      CUSTOM: {
        name: 'Custom Katana Crawl',
        description: 'Define your own katana parameters',
        parameters: {
          scan_type: 'custom',
          intensity: 'medium'
        }
      }
    }
  },
  GAU: {
    name: 'GAU URL Enumeration',
    category: 'Reconnaissance',
    templates: {
      QUICK_ENUM: {
        name: 'Quick URL Enumeration',
        description: 'Fetch known URLs from Wayback Machine and other sources',
        parameters: {
          scan_type: 'quick',
          intensity: 'low'
        }
      },
      COMPREHENSIVE: {
        name: 'Comprehensive URL Enumeration',
        description: 'All providers with subdomain inclusion',
        parameters: {
          providers: 'wayback,commoncrawl,otx,urlscan',
          subs: true,
          scan_type: 'comprehensive',
          intensity: 'medium'
        }
      },
      CUSTOM: {
        name: 'Custom GAU Scan',
        description: 'Define your own GAU parameters',
        parameters: {
          scan_type: 'custom',
          intensity: 'medium'
        }
      }
    }
  },
  CUSTOM_TOOL: {
    name: 'Custom Tool',
    category: 'Other',
    templates: {
      CUSTOM: {
        name: 'Custom Command',
        description: 'Run any custom command or tool',
        parameters: {
          command: '',
          scan_type: 'custom',
          intensity: 'medium'
        }
      }
    }
  },
  CHAIN_TASK: {
    name: 'Chain Task',
    category: 'Workflow',
    templates: {
      BASIC_CHAIN: {
        name: 'Basic Recon Chain',
        description: 'Subfinder → HTTPX → Nuclei → Screenshots',
        parameters: {
          steps: [
            {
              id: 'step1',
              type: 'subfinder',
              name: 'Subdomain Enumeration',
              command: 'subfinder -d {target} -silent',
              output_type: 'domains'
            },
            {
              id: 'step2',
              type: 'httpx',
              name: 'HTTP Discovery',
              command: 'httpx -l {input} -timeout 10 -threads 50',
              input_from: 'step1',
              output_type: 'urls'
            },
            {
              id: 'step3',
              type: 'nuclei',
              name: 'Vulnerability Scan',
              command: 'nuclei -t cve,default-logins,exposures -severity critical,high -l {input}',
              input_from: 'step2',
              output_type: 'findings'
            },
            {
              id: 'step4',
              type: 'screenshot',
              name: 'HTTPX Screenshots',
              command: 'httpx -l {input} -ss -timeout 20 -threads 25 -screenshot-dir screenshots',
              input_from: 'step2',
              output_type: 'screenshots'
            }
          ],
          scan_type: 'chain',
          intensity: 'medium'
        }
      },
      ADVANCED_CHAIN: {
        name: 'Advanced Attack Chain',
        description: 'Subfinder → HTTPX → Nuclei → Screenshots → AI Fuzz',
        parameters: {
          steps: [
            {
              id: 'step1',
              type: 'subfinder',
              name: 'Subdomain Enumeration',
              command: 'subfinder -d {target} -all -silent',
              output_type: 'domains'
            },
            {
              id: 'step2',
              type: 'httpx',
              name: 'HTTP Discovery',
              command: 'httpx -l {input} -timeout 15 -threads 30 -ports 80,443,8080,8443,3000,5000,8000,8888',
              input_from: 'step1',
              output_type: 'urls'
            },
            {
              id: 'step3',
              type: 'nuclei',
              name: 'Vulnerability Scan',
              command: 'nuclei -t all -severity critical,high,medium -l {input}',
              input_from: 'step2',
              output_type: 'findings'
            },
            {
              id: 'step4',
              type: 'screenshot',
              name: 'HTTPX Screenshots',
              command: 'httpx -l {input} -ss -timeout 20 -threads 25 -screenshot-dir screenshots',
              input_from: 'step2',
              output_type: 'screenshots'
            },
            {
              id: 'step5',
              type: 'ai_fuzz',
              name: 'AI Fuzzing',
              command: 'ffuf -u {base_url}/FUZZ -w /usr/share/wordlists/dirb/common.txt -mc 200,204,301,302,307,401,403',
              input_from: 'step2',
              output_type: 'urls'
            }
          ],
          scan_type: 'chain',
          intensity: 'high'
        }
      },
      RECON_CHAIN: {
        name: 'Full Recon Chain',
        description: 'Subfinder -> HTTPX -> Warden -> Screenshots -> Katana -> JSHonker -> GAU',
        parameters: {
          steps: [
            {
              id: 'step1',
              type: 'subfinder',
              name: 'Subdomain Enumeration',
              command: 'subfinder -d {target} -all -silent',
              output_type: 'domains'
            },
            {
              id: 'step2',
              type: 'httpx',
              name: 'HTTP Discovery',
              command: 'httpx -l {input} -timeout 15 -threads 30',
              input_from: 'step1',
              output_type: 'urls'
            },
            {
              id: 'step3',
              type: 'warden',
              name: 'Fingerprint & Functionality',
              command: 'warden',
              input_from: 'step2',
              output_type: 'findings'
            },
            {
              id: 'step4',
              type: 'screenshot',
              name: 'Screenshots',
              command: 'httpx -l {input} -ss -timeout 20 -threads 25 -screenshot-dir screenshots',
              input_from: 'step2',
              output_type: 'screenshots'
            },
            {
              id: 'step5',
              type: 'katana',
              name: 'Web Crawl',
              command: 'katana -list {input} -d 3 -jc -silent',
              input_from: 'step2',
              output_type: 'urls'
            },
            {
              id: 'step6',
              type: 'jshonker',
              name: 'JS Analysis',
              command: 'jshonker -j',
              input_from: 'step5',
              output_type: 'findings'
            },
            {
              id: 'step7',
              type: 'gau',
              name: 'Historical URLs',
              command: 'gau --subs {target}',
              input_from: 'initial',
              output_type: 'urls'
            }
          ],
          scan_type: 'chain',
          intensity: 'high'
        }
      },
      CUSTOM_CHAIN: {
        name: 'Custom Chain',
        description: 'Build your own multi-step workflow',
        parameters: {
          steps: [],
          scan_type: 'chain',
          intensity: 'medium'
        }
      }
    }
  }
};

const TASK_CATEGORIES = {
  RECON: {
    name: 'Reconnaissance',
    tasks: {
      SUBFINDER_SCAN: 'SUBFINDER_SCAN',
      KATANA_SCAN: 'KATANA_SCAN',
      NAABU_SCAN: 'NAABU_SCAN',
      GAU: 'GAU'
    }
  },
  SCANNING: {
    name: 'Scanning',
    tasks: {
      NMAP_SCAN: 'NMAP_SCAN',
      HTTPX_SCAN: 'HTTPX_SCAN',
      NUCLEI_SCAN: 'NUCLEI_SCAN',
      SQLMAP_SCAN: 'SQLMAP_SCAN',
      FFUF: 'FFUF',
      WARDEN: 'WARDEN'
    }
  },
  WORKFLOW: {
    name: 'Workflow',
    tasks: {
      CHAIN_TASK: 'CHAIN_TASK'
    }
  },
  OTHER: {
    name: 'Other',
    tasks: {
      CUSTOM_TOOL: 'CUSTOM_TOOL',
      AI_FUZZ: 'AI_FUZZ'
    }
  }
};

const STATUS_COLORS = {
  PENDING: 'blue',
  RUNNING: 'orange',
  COMPLETED: 'green',
  FAILED: 'red',
  CANCELLED: 'neutral'
};

const getTaskCategory = (taskType) => {
  for (const [categoryKey, category] of Object.entries(TASK_CATEGORIES)) {
    if (Object.values(category.tasks).includes(taskType)) {
      return category.name;
    }
  }
  return 'Unknown';
};

const getTaskTemplates = (taskType) => {
  return TASK_TEMPLATES[taskType]?.templates || {};
};

const getTemplateParameters = (taskType, template) => {
  return TASK_TEMPLATES[taskType]?.templates[template]?.parameters || {};
};

const getIntensityColor = (intensity) => {
  const colors = {
    'low': 'green',
    'medium': 'yellow',
    'high': 'red'
  };
  return colors[intensity] || 'neutral';
};

const AVAILABLE_STEPS = {
  subfinder: {
    name: 'Subfinder',
    description: 'Subdomain enumeration',
    icon: '🔍',
    output_type: 'domains'
  },
  httpx: {
    name: 'HTTPX',
    description: 'HTTP probing and screenshots',
    icon: '🌐',
    output_type: 'urls'
  },
  nuclei: {
    name: 'Nuclei',
    description: 'Vulnerability scanning',
    icon: '⚡',
    output_type: 'findings'
  },
  nmap: {
    name: 'Nmap',
    description: 'Port scanning',
    icon: '🌐',
    output_type: 'hosts'
  },
  screenshot: {
    name: 'HTTPX Screenshot',
    description: 'Web screenshots with HTTPX -ss',
    icon: '📸',
    output_type: 'screenshots'
  },
  ai_fuzz: {
    name: 'AI Fuzz',
    description: 'AI-powered fuzzing',
    icon: '🤖',
    output_type: 'urls'
  },
  ffuf: {
    name: 'FFuF',
    description: 'Directory fuzzing',
    icon: '🔍',
    output_type: 'urls'
  },
  katana: {
    name: 'Katana',
    description: 'Web crawling',
    icon: '🕷️',
    output_type: 'urls'
  },
  gau: {
    name: 'GAU',
    description: 'Historical URL enumeration',
    icon: '📜',
    output_type: 'urls'
  },
  jshonker: {
    name: 'JSHonker',
    description: 'JS file analysis (endpoints, secrets, emails)',
    icon: '📜',
    output_type: 'findings'
  },
  warden: {
    name: 'Warden',
    description: 'Tech fingerprint & functionality detection',
    icon: '🛡️',
    output_type: 'findings'
  },
  custom: {
    name: 'Custom',
    description: 'Custom command',
    icon: '⚙️',
    output_type: 'custom'
  }
};

const generateStepId = () => `step_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

function Tasks() {
  const { currentProject } = useProject();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTaskDialog, setShowNewTaskDialog] = useState(false);
  const [showAutomatedTasks, setShowAutomatedTasks] = useState(true);
  const [showManualTasks, setShowManualTasks] = useState(true);
  const [newTask, setNewTask] = useState({
    name: '',
    task_type: 'NMAP_SCAN',
    template: 'INITIAL_SCAN',
    target: '',
    parameters: {}
  });

  // Chain builder state
  const [showChainBuilder, setShowChainBuilder] = useState(false);
  const [chainSteps, setChainSteps] = useState([]);
  const [editingStep, setEditingStep] = useState(null);
  const [showStepEditor, setShowStepEditor] = useState(false);
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Workflow state
  const [workflows, setWorkflows] = useState([]);
  const [showWorkflowManager, setShowWorkflowManager] = useState(false);
  const [showNewWorkflowDialog, setShowNewWorkflowDialog] = useState(false);
  const [newWorkflow, setNewWorkflow] = useState({
    name: '',
    description: '',
    config: {
      steps: []
    }
  });
  const [workflowExamples, setWorkflowExamples] = useState({
    examples: {
      basic_recon: {
        name: 'Basic Reconnaissance',
        description: 'Subfinder → HTTPX → Nuclei → Screenshots',
        config: {
          steps: [
            {
              id: 'step1',
              task_type: 'SUBFINDER_SCAN',
              name: 'Subdomain Enumeration',
              template: 'QUICK_SUBDOMAINS',
              execution_mode: 'single',
              input_source: 'target'
            },
            {
              id: 'step2',
              task_type: 'HTTPX_SCAN',
              name: 'HTTP Discovery',
              template: 'QUICK_HTTP',
              execution_mode: 'batch',
              input_source: 'step1_output'
            },
            {
              id: 'step3',
              task_type: 'NUCLEI_SCAN',
              name: 'Vulnerability Scan',
              template: 'QUICK_SCAN',
              execution_mode: 'batch',
              input_source: 'step2_output'
            },
            {
              id: 'step4',
              task_type: 'HTTPX_SCAN',
              name: 'Screenshot Capture',
              template: 'SCREENSHOT',
              execution_mode: 'batch',
              input_source: 'step2_output'
            }
          ]
        }
      }
    },
    execution_modes: {
      single: 'Execute once with the initial target',
      batch: 'Execute for each output from the previous step',
      parallel: 'Execute multiple steps in parallel'
    }
  });

  const [showWorkflowStatusDialog, setShowWorkflowStatusDialog] = useState(false);
  const [workflowStatus, setWorkflowStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState('');
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);

  // Task detail state
  const [showTaskDetail, setShowTaskDetail] = useState(false);
  const [taskDetail, setTaskDetail] = useState(null);
  const [taskDetailLoading, setTaskDetailLoading] = useState(false);

  const fetchTasks = async () => {
    if (!currentProject?.id) return;
    
    try {
      const response = await api.get(`/tasks/${currentProject.id}`);
      setTasks(response.data);
    } catch (error) {
      toaster.danger('Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  };

  const fetchTaskDetail = async (taskId) => {
    if (!currentProject?.id) return;
    setTaskDetailLoading(true);
    try {
      const response = await api.get(`/tasks/${currentProject.id}/${taskId}`);
      setTaskDetail(response.data);
      setShowTaskDetail(true);
    } catch (error) {
      toaster.danger('Failed to fetch task details');
    } finally {
      setTaskDetailLoading(false);
    }
  };

  const fetchWorkflows = async () => {
    if (!currentProject?.id) return;
    
    try {
      const response = await api.get(`/workflows/${currentProject.id}`);
      setWorkflows(response.data);
    } catch (error) {
      console.error('Failed to fetch workflows:', error);
    }
  };

  const fetchWorkflowExamples = async () => {
    if (!currentProject?.id) return;
    
    try {
      const response = await api.get(`/workflows/${currentProject.id}/examples`);
      setWorkflowExamples(response.data);
    } catch (error) {
      console.error('Failed to fetch workflow examples:', error);
      // Set default examples if API fails
      setWorkflowExamples({
        examples: {
          basic_recon: {
            name: 'Basic Reconnaissance',
            description: 'Subfinder → HTTPX → Nuclei → Screenshots',
            config: {
              steps: [
                {
                  id: 'step1',
                  task_type: 'SUBFINDER_SCAN',
                  name: 'Subdomain Enumeration',
                  template: 'QUICK_SUBDOMAINS',
                  execution_mode: 'single',
                  input_source: 'target'
                },
                {
                  id: 'step2',
                  task_type: 'HTTPX_SCAN',
                  name: 'HTTP Discovery',
                  template: 'QUICK_HTTP',
                  execution_mode: 'batch',
                  input_source: 'step1_output'
                },
                {
                  id: 'step3',
                  task_type: 'NUCLEI_SCAN',
                  name: 'Vulnerability Scan',
                  template: 'QUICK_SCAN',
                  execution_mode: 'batch',
                  input_source: 'step2_output'
                },
                {
                  id: 'step4',
                  task_type: 'HTTPX_SCAN',
                  name: 'Screenshot Capture',
                  template: 'SCREENSHOT',
                  execution_mode: 'batch',
                  input_source: 'step2_output'
                }
              ]
            }
          },
          advanced_attack: {
            name: 'Advanced Attack Chain',
            description: 'Comprehensive reconnaissance and vulnerability assessment',
            config: {
              steps: [
                {
                  id: 'step1',
                  task_type: 'SUBFINDER_SCAN',
                  name: 'Comprehensive Subdomain Enumeration',
                  template: 'COMPREHENSIVE',
                  execution_mode: 'single',
                  input_source: 'target'
                },
                {
                  id: 'step2',
                  task_type: 'HTTPX_SCAN',
                  name: 'Full HTTP Discovery',
                  template: 'FULL_HTTP',
                  execution_mode: 'batch',
                  input_source: 'step1_output'
                },
                {
                  id: 'step3',
                  task_type: 'NUCLEI_SCAN',
                  name: 'Comprehensive Vulnerability Scan',
                  template: 'FULL_SCAN',
                  execution_mode: 'batch',
                  input_source: 'step2_output'
                },
                {
                  id: 'step4',
                  task_type: 'HTTPX_SCAN',
                  name: 'Screenshot Capture',
                  template: 'SCREENSHOT',
                  execution_mode: 'batch',
                  input_source: 'step2_output'
                }
              ]
            }
          }
        },
        execution_modes: {
          single: 'Execute once with the initial target',
          batch: 'Execute for each output from the previous step',
          parallel: 'Execute multiple steps in parallel'
        }
      });
    }
  };

  useEffect(() => {
    if (currentProject) {
      fetchTasks();
      fetchWorkflows();
      fetchWorkflowExamples();
      // Refresh tasks every 5 seconds
      const interval = setInterval(fetchTasks, 5000);
      return () => clearInterval(interval);
    }
  }, [currentProject]);

  const handleCreateTask = async () => {
    if (!currentProject?.id) return;
    
    try {
      // Validate inputs
      if (!newTask.name.trim()) {
        toaster.danger('Task name is required');
        return;
      }
      if (!newTask.target.trim()) {
        toaster.danger('Target is required');
        return;
      }

      // Prepare parameters with target substitution
      const parameters = { ...newTask.parameters };
      
      if (newTask.task_type === 'CHAIN_TASK') {
        // For chain tasks, use the built chain steps
        parameters.steps = chainSteps.map(step => ({
          ...step,
          command: step.command.replace('{target}', newTask.target.trim())
        }));
      } else if (parameters.command) {
        parameters.command = parameters.command.replace('{target}', newTask.target.trim());
      }

      const response = await api.post(`/tasks/${currentProject.id}`, {
        name: newTask.name.trim(),
        task_type: newTask.task_type,
        target: newTask.target.trim(),
        template: newTask.template,
        parameters: parameters
      });

      toaster.success('Task created successfully');
      setShowNewTaskDialog(false);
      setNewTask({
        name: '',
        task_type: 'NMAP_SCAN',
        template: 'INITIAL_SCAN',
        target: '',
        parameters: {}
      });
      fetchTasks();
    } catch (error) {
      console.error('Error creating task:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to create task';
      toaster.danger(typeof errorMessage === 'string' ? errorMessage : 'Failed to create task');
    }
  };

  const handleTaskAction = async (action, taskId) => {
    if (!currentProject?.id) return;
    
    try {
      switch (action) {
        case 'cancel':
          await api.post(`/tasks/${currentProject.id}/${taskId}/cancel`);
          toaster.success('Task cancelled successfully');
          break;
        case 'delete':
          await api.delete(`/tasks/${currentProject.id}/${taskId}`);
          setTasks(tasks.filter(task => task.id !== taskId));
          toaster.success('Task deleted successfully');
          break;
        case 'restart':
          await api.post(`/tasks/${currentProject.id}/${taskId}/restart`);
          toaster.success('Task restarted successfully');
          break;
        default:
          break;
      }
      if (action !== 'delete') {
        fetchTasks();
      }
    } catch (error) {
      console.error(`Error performing ${action}:`, error);
      const errorMessage = error.response?.data?.detail || error.message || `Failed to ${action} task`;
      toaster.danger(typeof errorMessage === 'string' ? errorMessage : `Failed to ${action} task`);
    }
  };

  const handleCreateWorkflow = async () => {
    if (!currentProject?.id) return;
    
    try {
      if (!newWorkflow.name.trim()) {
        toaster.danger('Workflow name is required');
        return;
      }

      const response = await api.post(`/workflows/${currentProject.id}`, {
        name: newWorkflow.name.trim(),
        description: newWorkflow.description.trim(),
        config: newWorkflow.config
      });

      toaster.success('Workflow created successfully');
      setShowNewWorkflowDialog(false);
      setNewWorkflow({
        name: '',
        description: '',
        config: {
          steps: []
        }
      });
      fetchWorkflows();
    } catch (error) {
      console.error('Error creating workflow:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to create workflow';
      toaster.danger(typeof errorMessage === 'string' ? errorMessage : 'Failed to create workflow');
    }
  };

  const handleExecuteWorkflow = async (workflowId, target) => {
    if (!currentProject?.id) return;
    
    try {
      const response = await api.post(`/workflows/${currentProject.id}/${workflowId}/execute`, {
        target: target
      });
      toaster.success('Workflow execution started successfully');
      fetchTasks(); // Refresh tasks to see the new workflow tasks
    } catch (error) {
      console.error('Error executing workflow:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to execute workflow';
      toaster.danger(typeof errorMessage === 'string' ? errorMessage : 'Failed to execute workflow');
    }
  };

  const handleDeleteWorkflow = async (workflowId) => {
    if (!currentProject?.id) return;
    
    try {
      await api.delete(`/workflows/${currentProject.id}/${workflowId}`);
      setWorkflows(workflows.filter(workflow => workflow.id !== workflowId));
      toaster.success('Workflow deleted successfully');
    } catch (error) {
      console.error('Error deleting workflow:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to delete workflow';
      toaster.danger(typeof errorMessage === 'string' ? errorMessage : 'Failed to delete workflow');
    }
  };

  const loadWorkflowExample = (exampleKey) => {
    const example = workflowExamples.examples?.[exampleKey];
    if (example) {
      setNewWorkflow({
        name: example.name || '',
        description: example.description || '',
        config: example.config || { steps: [] }
      });
      toaster.success(`Loaded workflow example: ${example.name}`);
    } else {
      toaster.danger('Failed to load workflow example');
    }
  };

  // Chain builder functions
  const addStep = (stepType) => {
    const newStep = {
      id: generateStepId(),
      type: stepType,
      name: AVAILABLE_STEPS[stepType].name,
      command: '',
      input_from: chainSteps.length > 0 ? chainSteps[chainSteps.length - 1].id : null,
      output_type: AVAILABLE_STEPS[stepType].output_type
    };
    setChainSteps([...chainSteps, newStep]);
  };

  const addStepAtPosition = (stepType, position) => {
    const newStep = {
      id: generateStepId(),
      type: stepType,
      name: AVAILABLE_STEPS[stepType].name,
      command: '',
      input_from: position > 0 ? chainSteps[position - 1]?.id : null,
      output_type: AVAILABLE_STEPS[stepType].output_type
    };
    
    const newSteps = [...chainSteps];
    newSteps.splice(position, 0, newStep);
    
    // Update input_from for steps after the inserted step
    for (let i = position + 1; i < newSteps.length; i++) {
      newSteps[i].input_from = newSteps[i - 1].id;
    }
    
    setChainSteps(newSteps);
  };

  const removeStep = (stepId) => {
    setChainSteps(chainSteps.filter(step => step.id !== stepId));
  };

  const moveStep = (stepId, direction) => {
    const index = chainSteps.findIndex(step => step.id === stepId);
    if (index === -1) return;

    const newSteps = [...chainSteps];
    if (direction === 'up' && index > 0) {
      [newSteps[index], newSteps[index - 1]] = [newSteps[index - 1], newSteps[index]];
    } else if (direction === 'down' && index < newSteps.length - 1) {
      [newSteps[index], newSteps[index + 1]] = [newSteps[index + 1], newSteps[index]];
    }
    setChainSteps(newSteps);
  };

  const editStep = (step) => {
    setEditingStep(step);
    setShowStepEditor(true);
  };

  const saveStep = (updatedStep) => {
    setChainSteps(chainSteps.map(step => 
      step.id === updatedStep.id ? updatedStep : step
    ));
    setShowStepEditor(false);
    setEditingStep(null);
  };

  const saveChainTemplate = (templateName) => {
    const template = {
      id: generateStepId(),
      name: templateName,
      steps: chainSteps,
      created_at: new Date().toISOString()
    };
    setSavedTemplates([...savedTemplates, template]);
    toaster.success('Chain template saved successfully');
  };

  const loadChainTemplate = (template) => {
    setChainSteps(template.steps);
    toaster.success(`Loaded template: ${template.name}`);
  };


  // Drag and drop handlers
  const handleDragStart = (e, stepType) => {
    e.dataTransfer.setData('stepType', stepType);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDragOverIndex('empty');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverIndex(null);
    const stepType = e.dataTransfer.getData('stepType');
    if (stepType) {
      addStep(stepType);
    }
  };

  const handleStepDrop = (e, targetIndex) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverIndex(null);
    const stepId = e.dataTransfer.getData('stepId');
    const stepType = e.dataTransfer.getData('stepType');

    if (stepId) {
      // Reordering existing step
      const currentIndex = chainSteps.findIndex(step => step.id === stepId);
      if (currentIndex !== -1 && currentIndex !== targetIndex) {
        const newSteps = [...chainSteps];
        const [movedStep] = newSteps.splice(currentIndex, 1);
        newSteps.splice(targetIndex, 0, movedStep);

        // Update input_from for all steps after the move
        for (let i = 1; i < newSteps.length; i++) {
          newSteps[i].input_from = newSteps[i - 1].id;
        }

        setChainSteps(newSteps);
      }
    } else if (stepType) {
      // Adding new step at specific position
      addStepAtPosition(stepType, targetIndex);
    }
  };

  const handleStepDragStart = (e, stepId) => {
    e.dataTransfer.setData('stepId', stepId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDragOverIndex(null);
  };

  const handleStepDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (index !== undefined) setDragOverIndex(index);
  };

  const handleCardDragOver = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const dropIndex = e.clientY < midY ? index : index + 1;
    setDragOverIndex(dropIndex);
  };

  const fetchWorkflowStatus = async (workflow) => {
    if (!currentProject?.id || !workflow?.id) return;
    setStatusLoading(true);
    setStatusError('');
    setWorkflowStatus(null);
    setSelectedWorkflow(workflow);
    setShowWorkflowStatusDialog(true);
    try {
      const response = await api.get(`/workflows/${currentProject.id}/${workflow.id}/status`);
      setWorkflowStatus(response.data);
    } catch (error) {
      setStatusError(error.response?.data?.detail || 'Failed to fetch workflow status');
    } finally {
      setStatusLoading(false);
    }
  };

  if (!currentProject) {
    return (
      <Pane padding={16}>
        Please select a project first.
      </Pane>
    );
  }

  if (loading) {
    return (
      <Pane display="flex" alignItems="center" justifyContent="center" height={400}>
        <Spinner />
      </Pane>
    );
  }

  // Filter tasks based on selection
  const filteredTasks = tasks.filter(task => {
    const isAutomated = task.is_automated === true;
    if (isAutomated && !showAutomatedTasks) return false;
    if (!isAutomated && !showManualTasks) return false;
    return true;
  });

  const automatedCount = tasks.filter(t => t.is_automated === true).length;
  const manualCount = tasks.filter(t => t.is_automated !== true).length;

  return (
    <Pane padding={16}>
      <Pane display="flex" justifyContent="space-between" marginBottom={16}>
        <h2>Tasks</h2>
        <Pane>
          <Button
            appearance="default"
            onClick={() => setShowWorkflowManager(true)}
            marginRight={8}
          >
            Workflows ({workflows.length})
          </Button>
          <Button appearance="primary" onClick={() => setShowNewTaskDialog(true)}>
            New Task
          </Button>
        </Pane>
      </Pane>

      <Pane display="flex" marginBottom={16} alignItems="center">
        <Text marginRight={12} fontWeight={500}>Filter:</Text>
        <Button
          marginRight={8}
          appearance={showManualTasks ? "primary" : "default"}
          onClick={() => setShowManualTasks(!showManualTasks)}
        >
          Manual Tasks ({manualCount})
        </Button>
        <Button
          appearance={showAutomatedTasks ? "primary" : "default"}
          onClick={() => setShowAutomatedTasks(!showAutomatedTasks)}
        >
          Automated Tasks ({automatedCount})
        </Button>
      </Pane>

      <Table>
        <Table.Head>
          <Table.TextHeaderCell>Name</Table.TextHeaderCell>
          <Table.TextHeaderCell>Category</Table.TextHeaderCell>
          <Table.TextHeaderCell>Type</Table.TextHeaderCell>
          <Table.TextHeaderCell>Template</Table.TextHeaderCell>
          <Table.TextHeaderCell>Target</Table.TextHeaderCell>
          <Table.TextHeaderCell>Status</Table.TextHeaderCell>
          <Table.TextHeaderCell>Created</Table.TextHeaderCell>
          <Table.TextHeaderCell></Table.TextHeaderCell>
        </Table.Head>
        <Table.Body>
          {filteredTasks.map(task => {
            const templateInfo = TASK_TEMPLATES[task.task_type]?.templates[task.template];
            return (
              <Table.Row key={task.id} isSelectable onSelect={() => fetchTaskDetail(task.id)}>
                <Table.TextCell>{task.name}</Table.TextCell>
                <Table.TextCell>
                  <Badge color="neutral" isSolid>
                    {getTaskCategory(task.task_type)}
                  </Badge>
                </Table.TextCell>
                <Table.TextCell>{task.task_type ? task.task_type.replace('_', ' ') : ''}</Table.TextCell>
                <Table.TextCell>
                  {templateInfo ? (
                    <Tooltip content={templateInfo.description || ''}>
                      <Text size={300}>{templateInfo.name || 'Unknown'}</Text>
                    </Tooltip>
                  ) : (
                    <Text size={300} color="muted">Custom</Text>
                  )}
                </Table.TextCell>
                <Table.TextCell>{task.target || ''}</Table.TextCell>
                <Table.TextCell>
                  <Badge color={STATUS_COLORS[task.status]}>
                    {task.status}
                  </Badge>
                </Table.TextCell>
                <Table.TextCell>
                  {task.created_at ? new Date(task.created_at).toLocaleString() : ''}
                </Table.TextCell>
                <Table.Cell onClick={(e) => e.stopPropagation()}>
                  <Popover
                    position={Position.BOTTOM_LEFT}
                    content={
                      <Menu>
                        <Menu.Group>
                          <Menu.Item
                            onClick={() => fetchTaskDetail(task.id)}
                          >
                            View Details
                          </Menu.Item>
                        </Menu.Group>
                        {['PENDING', 'RUNNING'].includes(task.status) && (
                          <Menu.Group>
                            <Menu.Item
                              icon={CrossIcon}
                              intent="danger"
                              onClick={() => handleTaskAction('cancel', task.id)}
                            >
                              Cancel
                            </Menu.Item>
                          </Menu.Group>
                        )}
                        <Menu.Group>
                          <Menu.Item
                            icon={TrashIcon}
                            intent="danger"
                            onClick={() => handleTaskAction('delete', task.id)}
                          >
                            Delete
                          </Menu.Item>
                        </Menu.Group>
                        {['FAILED', 'CANCELLED'].includes(task.status) && (
                          <Menu.Group>
                            <Menu.Item
                              icon={RefreshIcon}
                              intent="success"
                              onClick={() => handleTaskAction('restart', task.id)}
                            >
                              Restart
                            </Menu.Item>
                          </Menu.Group>
                        )}
                      </Menu>
                    }
                  >
                    <Button marginY={8} marginRight={12} iconAfter={CaretDownIcon}>
                      Actions
                    </Button>
                  </Popover>
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>

      <Dialog
        isShown={showNewTaskDialog}
        title="Create New Task"
        onCloseComplete={() => setShowNewTaskDialog(false)}
        confirmLabel="Create Task"
        onConfirm={handleCreateTask}
        width={600}
      >
        <TextInputField
          label="Task Name"
          placeholder="Enter a unique task name"
          value={newTask.name}
          onChange={e => setNewTask({ ...newTask, name: e.target.value })}
          required
        />
        
        <SelectField
          label="Task Type"
          value={newTask.task_type}
          onChange={e => {
            const taskType = e.target.value;
            const templates = getTaskTemplates(taskType);
            const firstTemplate = Object.keys(templates)[0];
            const params = getTemplateParameters(taskType, firstTemplate);
            setNewTask({
              ...newTask,
              task_type: taskType,
              template: firstTemplate,
              parameters: params
            });
            // Populate chainSteps from template if it has steps
            if (params.steps && params.steps.length > 0) {
              setChainSteps(params.steps);
            } else {
              setChainSteps([]);
            }
          }}
        >
          {Object.entries(TASK_CATEGORIES).map(([categoryKey, category]) => (
            <optgroup key={categoryKey} label={category.name}>
              {Object.entries(category.tasks).map(([key, value]) => (
                <option key={key} value={value}>
                  {key.replace('_', ' ')}
                </option>
              ))}
            </optgroup>
          ))}
        </SelectField>

        {TASK_TEMPLATES[newTask.task_type] && (
          <Pane>
            <SelectField
              label="Template"
              value={newTask.template}
              onChange={e => {
                const template = e.target.value;
                const params = getTemplateParameters(newTask.task_type, template);
                setNewTask({
                  ...newTask,
                  template,
                  parameters: params
                });
                // Populate chainSteps from template if it has steps
                if (params.steps && params.steps.length > 0) {
                  setChainSteps(params.steps);
                } else {
                  setChainSteps([]);
                }
              }}
            >
              {Object.entries(getTaskTemplates(newTask.task_type)).map(([key, template]) => (
                <option key={key} value={key}>
                  {template.name}
                </option>
              ))}
            </SelectField>
            {newTask.task_type === 'CHAIN_TASK' && (
              <Button
                appearance="primary"
                iconBefore={PlusIcon}
                onClick={() => setShowChainBuilder(true)}
                marginTop={8}
              >
                {chainSteps.length > 0 ? `Edit Chain (${chainSteps.length} steps)` : 'Build Chain'}
              </Button>
            )}
          </Pane>
        )}

        {newTask.template && TASK_TEMPLATES[newTask.task_type]?.templates[newTask.template] && (
          <Pane marginBottom={16}>
            <Text size={300} color="muted" marginBottom={4}>
              {TASK_TEMPLATES[newTask.task_type]?.templates[newTask.template]?.description || ''}
            </Text>
          </Pane>
        )}

        {newTask.parameters?.command && (
          <Pane marginBottom={16}>
            <Text size={300} color="muted" marginBottom={8}>
              Command Preview
            </Text>
            <Pane 
              padding={12} 
              background="tint1" 
              borderRadius={4}
              border="default"
              maxHeight={120}
              overflow="auto"
            >
              <Text fontFamily="mono" size={300} whiteSpace="pre-wrap" wordBreak="break-all">
                {(newTask.parameters.command || '').replace('{target}', newTask.target || '<target>')}
              </Text>
            </Pane>
            {newTask.parameters.intensity && (
              <Pane marginTop={8}>
                <Badge color={getIntensityColor(newTask.parameters.intensity)}>
                  Intensity: {newTask.parameters.intensity}
                </Badge>
              </Pane>
            )}
          </Pane>
        )}

        <Pane marginBottom={16}>
          <Text size={400} fontWeight={500} display="block" marginBottom={4}>Target</Text>
          <Pane display="flex" gap={8} alignItems="center">
            <Pane flex={1}>
              <TextInputField
                placeholder="Enter target (e.g., domain.com, 192.168.1.1)"
                value={newTask.target}
                onChange={e => setNewTask({ ...newTask, target: e.target.value })}
                required
                marginBottom={0}
              />
            </Pane>
            {(newTask.task_type === 'WARDEN' || newTask.task_type === 'HTTPX_SCAN') && (
              <Button
                size="small"
                appearance={newTask.target === '*' ? 'primary' : 'default'}
                onClick={() => setNewTask({ ...newTask, target: newTask.target === '*' ? '' : '*' })}
                marginTop={-4}
              >
                {newTask.target === '*' ? '★ All Domains' : '☆ All Domains'}
              </Button>
            )}
          </Pane>
          {newTask.target === '*' && (
            <Text size={300} color="muted" marginTop={4} display="block">
              Will scan all domains currently in this project
            </Text>
          )}
        </Pane>

        <Checkbox
          label="Filter out-of-scope results"
          checked={newTask.parameters?.filter_out_of_scope || false}
          onChange={e => setNewTask({
            ...newTask,
            parameters: {
              ...newTask.parameters,
              filter_out_of_scope: e.target.checked
            }
          })}
          marginBottom={16}
        />

        {newTask.template === 'CUSTOM' && (
          <TextInputField
            label="Custom Command"
            placeholder="Enter your custom command"
            value={newTask.parameters.command || ''}
            onChange={e => setNewTask({ 
              ...newTask, 
              parameters: { 
                ...newTask.parameters, 
                command: e.target.value 
              }
            })}
            required
          />
        )}
      </Dialog>

      {/* Chain Builder Dialog */}
      <Dialog
        isShown={showChainBuilder}
        title="Chain Task Builder"
        onCloseComplete={() => setShowChainBuilder(false)}
        width={800}
        height={600}
      >
        <Pane display="flex" height="100%">
          {/* Available Steps Panel */}
          <Pane width={200} borderRight="default" padding={16}>
            <Heading size={400} marginBottom={16}>Available Steps</Heading>
            <Text size={300} color="muted" marginBottom={16}>Drag steps to the right or click to add</Text>
            {Object.entries(AVAILABLE_STEPS).map(([key, step]) => (
              <Card
                key={key}
                padding={12}
                marginBottom={8}
                draggable
                onDragStart={(e) => handleDragStart(e, key)}
                onDragEnd={handleDragEnd}
                onClick={() => addStep(key)}
                elevation={1}
                cursor="grab"
                _hover={{ cursor: 'grabbing' }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.transition = 'transform 0.2s ease';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <Pane display="flex" alignItems="center">
                  <Text size={24} marginRight={8}>{step.icon}</Text>
                  <Pane>
                    <Text size={400} fontWeight={500}>{step.name}</Text>
                    <Text size={300} color="muted">{step.description || ''}</Text>
                  </Pane>
                </Pane>
              </Card>
            ))}
          </Pane>

          {/* Chain Steps Panel */}
          <Pane flex={1} padding={16}>
            <Pane display="flex" justifyContent="space-between" alignItems="center" marginBottom={16}>
              <Heading size={400}>Chain Steps</Heading>
              <Pane>
                <Popover
                  position={Position.BOTTOM_LEFT}
                  content={
                    <Menu>
                      {Object.entries(AVAILABLE_STEPS).map(([key, step]) => (
                        <Menu.Item
                          key={key}
                          onSelect={() => addStep(key)}
                        >
                          {step.icon} {step.name}
                        </Menu.Item>
                      ))}
                    </Menu>
                  }
                >
                  <Button
                    appearance="primary"
                    iconBefore={PlusIcon}
                    marginRight={8}
                  >
                    Add Step
                  </Button>
                </Popover>
                <Button
                  appearance="default"
                  iconBefore={PlusIcon}
                  onClick={() => setShowTemplateManager(true)}
                  marginRight={8}
                >
                  Templates
                </Button>
                <Button
                  appearance="default"
                  onClick={() => {
                    if (chainSteps.length > 0) {
                      const templateName = prompt('Enter template name:');
                      if (templateName) {
                        saveChainTemplate(templateName);
                      }
                    }
                  }}
                >
                  Save Template
                </Button>
              </Pane>
            </Pane>

            {chainSteps.length === 0 ? (
              <Pane
                display="flex"
                alignItems="center"
                justifyContent="center"
                height={200}
                background={dragOverIndex === 'empty' ? 'blueTint' : 'tint1'}
                borderRadius={4}
                onDragOver={handleDragOver}
                onDragLeave={() => setDragOverIndex(null)}
                onDrop={handleDrop}
                border="2px dashed"
                borderColor={dragOverIndex === 'empty' ? '#3366FF' : '#C1C4D6'}
              >
                <Text size={500} color="muted">Drop steps here to build your chain</Text>
              </Pane>
            ) : (
              <Pane
                onDragOver={(e) => {
                  e.preventDefault();
                  if (e.target === e.currentTarget) {
                    setDragOverIndex(chainSteps.length);
                  }
                }}
                onDragLeave={(e) => {
                  if (e.target === e.currentTarget) setDragOverIndex(null);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOverIndex(null);
                  const stepType = e.dataTransfer.getData('stepType');
                  const stepId = e.dataTransfer.getData('stepId');
                  if (!stepId && stepType) {
                    addStep(stepType);
                  }
                }}
              >
                {/* Drop zone at the top */}
                <Pane
                  height={20}
                  marginBottom={8}
                  onDragOver={(e) => handleStepDragOver(e, -1)}
                  onDragLeave={() => setDragOverIndex(null)}
                  onDrop={(e) => handleStepDrop(e, 0)}
                  border="2px dashed"
                  borderColor={dragOverIndex === -1 ? '#3366FF' : 'transparent'}
                  background={dragOverIndex === -1 ? '#EBF0FF' : 'transparent'}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Text size={300} color="muted">Drop new steps here</Text>
                </Pane>
                {chainSteps.map((step, index) => (
                  <Pane key={step.id}>
                    {/* Drop zone above step */}
                    <Pane
                      height={dragOverIndex === index ? 24 : 8}
                      marginBottom={4}
                      onDragOver={(e) => handleStepDragOver(e, index)}
                      onDragLeave={() => setDragOverIndex(null)}
                      onDrop={(e) => handleStepDrop(e, index)}
                      border="1px dashed"
                      borderColor={dragOverIndex === index ? '#3366FF' : 'transparent'}
                      background={dragOverIndex === index ? '#EBF0FF' : 'transparent'}
                      borderRadius={4}
                      transition="all 0.15s ease"
                    />
                    
                    <Card
                      padding={16}
                      marginBottom={12}
                      elevation={2}
                      border="default"
                      draggable
                      onDragStart={(e) => handleStepDragStart(e, step.id)}
                      onDragEnd={handleDragEnd}
                      onDragOver={(e) => handleCardDragOver(e, index)}
                      onDrop={(e) => handleStepDrop(e, dragOverIndex != null ? dragOverIndex : index)}
                      cursor="grab"
                      _hover={{ cursor: 'grabbing' }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.transition = 'transform 0.2s ease';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                      }}
                    >
                      <Pane display="flex" alignItems="center" justifyContent="space-between">
                        <Pane display="flex" alignItems="center" flex={1}>
                          <Text size={24} marginRight={12}>{AVAILABLE_STEPS[step.type]?.icon || '⚙️'}</Text>
                          <Pane flex={1}>
                            <Text size={400} fontWeight={500}>{step.name || 'Unnamed Step'}</Text>
                            <Text size={300} color="muted">{step.type || ''}</Text>
                            {step.input_from && (
                              <Text size={300} color="blue">Input from: {chainSteps.find(s => s.id === step.input_from)?.name || 'Previous step'}</Text>
                            )}
                          </Pane>
                        </Pane>
                        <Pane display="flex" alignItems="center">
                          <IconButton
                            icon={ArrowUpIcon}
                            size="small"
                            disabled={index === 0}
                            onClick={() => moveStep(step.id, 'up')}
                            marginRight={4}
                          />
                          <IconButton
                            icon={ArrowDownIcon}
                            size="small"
                            disabled={index === chainSteps.length - 1}
                            onClick={() => moveStep(step.id, 'down')}
                            marginRight={8}
                          />
                          <IconButton
                            icon={EditIcon}
                            size="small"
                            onClick={() => editStep(step)}
                            marginRight={4}
                          />
                          <IconButton
                            icon={TrashIcon}
                            size="small"
                            intent="danger"
                            onClick={() => removeStep(step.id)}
                          />
                        </Pane>
                      </Pane>
                    </Card>
                  </Pane>
                ))}
                
                {/* Drop zone at the end */}
                <Pane
                  height={dragOverIndex === chainSteps.length ? 24 : 8}
                  marginTop={4}
                  onDragOver={(e) => handleStepDragOver(e, chainSteps.length)}
                  onDragLeave={() => setDragOverIndex(null)}
                  onDrop={(e) => handleStepDrop(e, chainSteps.length)}
                  border="1px dashed"
                  borderColor={dragOverIndex === chainSteps.length ? '#3366FF' : 'transparent'}
                  background={dragOverIndex === chainSteps.length ? '#EBF0FF' : 'transparent'}
                  borderRadius={4}
                  transition="all 0.15s ease"
                />
              </Pane>
            )}
          </Pane>
        </Pane>
      </Dialog>

      {/* Step Editor Dialog */}
      <Dialog
        isShown={showStepEditor}
        title="Edit Step"
        onCloseComplete={() => {
          setShowStepEditor(false);
          setEditingStep(null);
        }}
        confirmLabel="Save Step"
        onConfirm={() => {
          if (editingStep) {
            saveStep(editingStep);
          }
        }}
        width={600}
      >
        {editingStep && (
          <Pane>
            <TextInputField
              label="Step Name"
              value={editingStep.name}
              onChange={e => setEditingStep({ ...editingStep, name: e.target.value })}
              required
            />
            <SelectField
              label="Input From"
              value={editingStep.input_from || ''}
              onChange={e => setEditingStep({ ...editingStep, input_from: e.target.value || null })}
            >
              <option value="">Initial target</option>
              {chainSteps.filter(step => step.id !== editingStep.id).map(step => (
                <option key={step.id} value={step.id}>
                  {step.name}
                </option>
              ))}
            </SelectField>
            <Textarea
              label="Command"
              placeholder="Enter the command to execute..."
              value={editingStep.command}
              onChange={e => setEditingStep({ ...editingStep, command: e.target.value })}
              rows={4}
            />
            <Text size={300} color="muted" marginTop={8}>
              Use {'{target}'} for the initial target, {'{input}'} for input from previous step, {'{output_dir}'} for output directory
            </Text>
          </Pane>
        )}
      </Dialog>

      {/* Template Manager Dialog */}
      <Dialog
        isShown={showTemplateManager}
        title="Chain Templates"
        onCloseComplete={() => setShowTemplateManager(false)}
        width={600}
      >
        <Pane>
          {savedTemplates.length === 0 ? (
            <Text size={400} color="muted">No saved templates yet</Text>
          ) : (
            savedTemplates.map(template => (
              <Card
                key={template.id}
                padding={16}
                marginBottom={12}
                isSelectable
                onSelect={() => {
                  loadChainTemplate(template);
                  setShowTemplateManager(false);
                }}
                elevation={1}
              >
                <Pane display="flex" justifyContent="space-between" alignItems="center">
                  <Pane>
                    <Text size={400} fontWeight={500}>{template.name || 'Unnamed Template'}</Text>
                                          <Text size={300} color="muted">
                        {template.steps.length} steps • Created {template.created_at ? new Date(template.created_at).toLocaleDateString() : 'Unknown'}
                      </Text>
                  </Pane>
                  <IconButton
                    icon={TrashIcon}
                    size="small"
                    intent="danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSavedTemplates(savedTemplates.filter(t => t.id !== template.id));
                    }}
                  />
                </Pane>
              </Card>
            ))
          )}
        </Pane>
      </Dialog>

      {/* Workflow Manager Dialog */}
      <Dialog
        isShown={showWorkflowManager}
        title="Workflow Manager"
        onCloseComplete={() => setShowWorkflowManager(false)}
        width={800}
        height={600}
      >
        <Pane display="flex" justifyContent="space-between" alignItems="center" marginBottom={16}>
          <Heading size={400}>Workflows</Heading>
          <Button appearance="primary" onClick={() => setShowNewWorkflowDialog(true)}>
            New Workflow
          </Button>
        </Pane>

        {workflows.length === 0 ? (
          <Pane
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height={200}
            background="tint1"
            borderRadius={4}
            padding={24}
          >
            <Text size={500} color="muted" marginBottom={16}>No workflows created yet</Text>
            <Text size={300} color="muted" marginBottom={16}>Create workflows to automate multi-step security scanning</Text>
            <Button appearance="primary" onClick={() => setShowNewWorkflowDialog(true)}>
              Create Your First Workflow
            </Button>
          </Pane>
        ) : (
          <Pane>
            {workflows.map(workflow => (
              <Card
                key={workflow.id}
                padding={16}
                marginBottom={12}
                elevation={1}
                border="default"
              >
                <Pane display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Pane flex={1}>
                    <Text size={400} fontWeight={500} marginBottom={4}>
                      {workflow.name}
                    </Text>
                    <Text size={300} color="muted" marginBottom={8}>
                      {workflow.description || 'No description'}
                    </Text>
                    <Pane display="flex" alignItems="center" marginBottom={8}>
                      <Badge 
                        color={workflow.status === 'ACTIVE' ? 'green' : workflow.status === 'DRAFT' ? 'neutral' : 'red'} 
                        marginRight={8}
                      >
                        {workflow.status}
                      </Badge>
                      <Text size={300} color="muted">
                        {workflow.config.steps?.length || 0} steps
                      </Text>
                    </Pane>
                    <Text size={300} color="muted">
                      Created {workflow.created_at ? new Date(workflow.created_at).toLocaleDateString() : 'Unknown'}
                    </Text>
                  </Pane>
                  <Pane display="flex" flexDirection="column" alignItems="flex-end">
                    <Button
                      appearance="primary"
                      size="small"
                      marginBottom={8}
                      disabled={workflow.status !== 'ACTIVE'}
                      onClick={() => {
                        const target = prompt('Enter target for workflow execution:');
                        if (target) {
                          handleExecuteWorkflow(workflow.id, target);
                          setShowWorkflowManager(false);
                        }
                      }}
                    >
                      Execute
                    </Button>
                    {workflow.status === 'DRAFT' ? (
                      <Button
                        appearance="default"
                        size="small"
                        marginBottom={8}
                        onClick={async () => {
                          try {
                            await api.post(`/workflows/${currentProject.id}/${workflow.id}/activate`);
                            toaster.success('Workflow activated');
                            fetchWorkflows();
                          } catch (error) {
                            console.error('Error activating workflow:', error);
                            toaster.danger('Failed to activate workflow');
                          }
                        }}
                      >
                        Activate
                      </Button>
                    ) : (
                      <Button
                        appearance="default"
                        size="small"
                        marginBottom={8}
                        onClick={async () => {
                          try {
                            await api.post(`/workflows/${currentProject.id}/${workflow.id}/deactivate`);
                            toaster.success('Workflow deactivated');
                            fetchWorkflows();
                          } catch (error) {
                            console.error('Error deactivating workflow:', error);
                            toaster.danger('Failed to deactivate workflow');
                          }
                        }}
                      >
                        Deactivate
                      </Button>
                    )}
                    <Button
                      appearance="default"
                      size="small"
                      marginBottom={8}
                      onClick={() => fetchWorkflowStatus(workflow)}
                    >
                      Status
                    </Button>
                    <Button
                      appearance="default"
                      size="small"
                      intent="danger"
                      onClick={() => {
                        if (confirm('Are you sure you want to delete this workflow?')) {
                          handleDeleteWorkflow(workflow.id);
                        }
                      }}
                    >
                      Delete
                    </Button>
                  </Pane>
                </Pane>
              </Card>
            ))}
          </Pane>
        )}
      </Dialog>

      {/* New Workflow Dialog */}
      <Dialog
        isShown={showNewWorkflowDialog}
        title="Create New Workflow"
        onCloseComplete={() => setShowNewWorkflowDialog(false)}
        confirmLabel="Create Workflow"
        onConfirm={handleCreateWorkflow}
        width={800}
        height={600}
      >
        <Pane display="flex" height="100%">
          {/* Workflow Examples Panel */}
          <Pane width={250} borderRight="default" padding={16}>
            <Heading size={400} marginBottom={16}>Workflow Examples</Heading>
            <Text size={300} color="muted" marginBottom={16}>Click to load an example</Text>
            {workflowExamples.examples && Object.keys(workflowExamples.examples).length > 0 ? (
              Object.entries(workflowExamples.examples).map(([key, example]) => (
                <Card
                  key={key}
                  padding={12}
                  marginBottom={8}
                  elevation={1}
                  cursor="pointer"
                  _hover={{ elevation: 2, border: '2px solid #1070CA' }}
                  onClick={() => loadWorkflowExample(key)}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.transition = 'transform 0.2s ease';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  <Text size={400} fontWeight={500} marginBottom={4}>
                    {example.name}
                  </Text>
                  <Text size={300} color="muted">
                    {example.description}
                  </Text>
                  <Text size={300} color="blue" marginTop={4}>
                    {example.config?.steps?.length || 0} steps
                  </Text>
                </Card>
              ))
            ) : (
              <Text size={300} color="muted">No examples available</Text>
            )}
          </Pane>

          {/* Workflow Configuration Panel */}
          <Pane flex={1} padding={16}>
            <Pane display="flex" justifyContent="space-between" alignItems="center" marginBottom={16}>
              <Heading size={400}>Workflow Configuration</Heading>
              <Pane>
                <Button
                  appearance="default"
                  size="small"
                  onClick={() => setNewWorkflow({
                    name: '',
                    description: '',
                    config: { steps: [] }
                  })}
                >
                  Clear
                </Button>
              </Pane>
            </Pane>
            
            <TextInputField
              label="Workflow Name"
              placeholder="Enter workflow name"
              value={newWorkflow.name}
              onChange={e => setNewWorkflow({ ...newWorkflow, name: e.target.value })}
              required
              marginBottom={16}
            />
            
            <Textarea
              label="Description"
              placeholder="Enter workflow description"
              value={newWorkflow.description}
              onChange={e => setNewWorkflow({ ...newWorkflow, description: e.target.value })}
              rows={3}
              marginBottom={16}
            />

            <Heading size={400} marginBottom={16}>Workflow Steps</Heading>
            {(newWorkflow.config?.steps?.length || 0) === 0 ? (
              <Pane
                display="flex"
                alignItems="center"
                justifyContent="center"
                height={100}
                background="tint1"
                borderRadius={4}
                border="2px dashed"
                borderColor="muted"
              >
                <Text size={400} color="muted">Load an example or create custom steps</Text>
              </Pane>
            ) : (
              <Pane>
                {(newWorkflow.config?.steps || []).map((step, index) => (
                  <Card
                    key={step.id || index}
                    padding={12}
                    marginBottom={8}
                    elevation={1}
                    border="default"
                  >
                    <Pane display="flex" alignItems="center" justifyContent="space-between">
                      <Pane flex={1}>
                        <Text size={400} fontWeight={500}>
                          Step {index + 1}: {step.name}
                        </Text>
                        <Text size={300} color="muted">
                          Type: {step.task_type} | Template: {step.template} | Mode: {step.execution_mode || 'single'}
                        </Text>
                        {step.input_source && (
                          <Text size={300} color="blue">
                            Input: {step.input_source}
                          </Text>
                        )}
                      </Pane>
                      <Pane display="flex" alignItems="center">
                        <Badge color="neutral" marginRight={8}>
                          {step.task_type?.replace('_', ' ')}
                        </Badge>
                      </Pane>
                    </Pane>
                  </Card>
                ))}
              </Pane>
            )}

            {workflowExamples.execution_modes && (
              <Pane marginTop={16}>
                <Heading size={300} marginBottom={8}>Execution Modes</Heading>
                {Object.entries(workflowExamples.execution_modes).map(([mode, description]) => (
                  <Text key={mode} size={300} color="muted" marginBottom={4}>
                    <strong>{mode}:</strong> {description}
                  </Text>
                ))}
              </Pane>
            )}
          </Pane>
        </Pane>
      </Dialog>

      {/* Workflow Status Dialog */}
      <Dialog
        isShown={showWorkflowStatusDialog}
        title={`Workflow Status${selectedWorkflow ? `: ${selectedWorkflow.name}` : ''}`}
        onCloseComplete={() => {
          setShowWorkflowStatusDialog(false);
          setWorkflowStatus(null);
          setSelectedWorkflow(null);
          setStatusError('');
        }}
        width={700}
      >
        {statusLoading ? (
          <Pane display="flex" alignItems="center" justifyContent="center" height={120}>
            <Spinner />
          </Pane>
        ) : statusError ? (
          <Text color="danger">{statusError}</Text>
        ) : workflowStatus ? (
          <Pane>
            <Text size={500} fontWeight={500} marginBottom={8}>
              Workflow: {workflowStatus.workflow_name}
            </Text>
            <Text size={400} marginBottom={8}>
              Workflow ID: {workflowStatus.workflow_id}
            </Text>
            {/* Render step/task status here */}
            <pre style={{ background: '#f9f9fb', padding: 12, borderRadius: 4, maxHeight: 300, overflow: 'auto' }}>
              {JSON.stringify(workflowStatus.status, null, 2)}
            </pre>
          </Pane>
        ) : (
          <Text>No status available.</Text>
        )}
      </Dialog>

      {/* Task Detail Dialog */}
      <Dialog
        isShown={showTaskDetail}
        title={taskDetail ? `Task: ${taskDetail.name}` : 'Task Details'}
        onCloseComplete={() => {
          setShowTaskDetail(false);
          setTaskDetail(null);
        }}
        hasFooter={false}
        width={800}
      >
        {taskDetailLoading ? (
          <Pane display="flex" justifyContent="center" padding={32}>
            <Spinner />
          </Pane>
        ) : taskDetail ? (
          <Pane>
            {/* Task Info */}
            <Pane display="flex" gap={8} marginBottom={16} flexWrap="wrap" alignItems="center">
              <Badge color={STATUS_COLORS[taskDetail.status]}>{taskDetail.status}</Badge>
              <Badge color="neutral">{taskDetail.task_type?.replace('_', ' ')}</Badge>
              <Text size={300} color="muted">Target: {taskDetail.target}</Text>
              {taskDetail.created_at && (
                <Text size={300} color="muted">
                  Created: {new Date(taskDetail.created_at).toLocaleString()}
                </Text>
              )}
              {taskDetail.completed_at && (
                <Text size={300} color="muted">
                  Completed: {new Date(taskDetail.completed_at).toLocaleString()}
                </Text>
              )}
            </Pane>

            {/* Summary Counters */}
            {taskDetail.results && (
              <Card padding={16} marginBottom={16} background="tint1">
                <Heading size={400} marginBottom={12}>Results Summary</Heading>
                <Pane display="flex" gap={16} flexWrap="wrap">
                  {taskDetail.results.urls && taskDetail.results.urls.length > 0 && (
                    <Pane textAlign="center" padding={12} background="white" borderRadius={4} minWidth={80}>
                      <Heading size={600} color="blue">{taskDetail.results.urls.length}</Heading>
                      <Text size={300} color="muted">URLs</Text>
                    </Pane>
                  )}
                  {taskDetail.results.domains && taskDetail.results.domains.length > 0 && (
                    <Pane textAlign="center" padding={12} background="white" borderRadius={4} minWidth={80}>
                      <Heading size={600} color="blue">{taskDetail.results.domains.length}</Heading>
                      <Text size={300} color="muted">Domains</Text>
                    </Pane>
                  )}
                  {taskDetail.results.ips && taskDetail.results.ips.length > 0 && (
                    <Pane textAlign="center" padding={12} background="white" borderRadius={4} minWidth={80}>
                      <Heading size={600} color="blue">{taskDetail.results.ips.length}</Heading>
                      <Text size={300} color="muted">Hosts</Text>
                    </Pane>
                  )}
                  {taskDetail.results.ports && taskDetail.results.ports.length > 0 && (
                    <Pane textAlign="center" padding={12} background="white" borderRadius={4} minWidth={80}>
                      <Heading size={600} color="blue">{taskDetail.results.ports.length}</Heading>
                      <Text size={300} color="muted">Ports</Text>
                    </Pane>
                  )}
                  {taskDetail.results.findings && taskDetail.results.findings.length > 0 && (
                    <Pane textAlign="center" padding={12} background="white" borderRadius={4} minWidth={80}>
                      <Heading size={600} color="red">{taskDetail.results.findings.length}</Heading>
                      <Text size={300} color="muted">Findings</Text>
                    </Pane>
                  )}
                  {(!taskDetail.results.urls || taskDetail.results.urls.length === 0) &&
                   (!taskDetail.results.domains || taskDetail.results.domains.length === 0) &&
                   (!taskDetail.results.ips || taskDetail.results.ips.length === 0) &&
                   (!taskDetail.results.ports || taskDetail.results.ports.length === 0) &&
                   (!taskDetail.results.findings || taskDetail.results.findings.length === 0) && (
                    <Text size={300} color="muted">No results recorded yet</Text>
                  )}
                </Pane>
              </Card>
            )}

            {/* Error */}
            {taskDetail.error && (
              <Card padding={16} marginBottom={16} background="#FEF2F2" border="1px solid #FECACA">
                <Heading size={400} marginBottom={8} color="#B91C1C">Error</Heading>
                <pre style={{
                  background: '#FFF5F5',
                  padding: 12,
                  borderRadius: 4,
                  maxHeight: 150,
                  overflow: 'auto',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  margin: 0,
                  color: '#B91C1C'
                }}>
                  {taskDetail.error}
                </pre>
              </Card>
            )}

            {/* Raw Output */}
            <Card padding={16} background="tint1">
              <Pane display="flex" justifyContent="space-between" alignItems="center" marginBottom={12}>
                <Heading size={400}>Raw Output</Heading>
                {taskDetail.status === 'RUNNING' && (
                  <Button
                    size="small"
                    appearance="minimal"
                    iconBefore={RefreshIcon}
                    onClick={() => fetchTaskDetail(taskDetail.id)}
                  >
                    Refresh
                  </Button>
                )}
              </Pane>
              {taskDetail.output ? (
                <pre style={{
                  background: '#1B2240',
                  color: '#E4E7EB',
                  padding: 16,
                  borderRadius: 4,
                  maxHeight: 400,
                  overflow: 'auto',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  margin: 0,
                  lineHeight: '1.5'
                }}>
                  {taskDetail.output}
                </pre>
              ) : (
                <Text size={300} color="muted">
                  {taskDetail.status === 'PENDING' ? 'Task has not started yet' :
                   taskDetail.status === 'RUNNING' ? 'Waiting for output...' :
                   'No output recorded'}
                </Text>
              )}
            </Card>
          </Pane>
        ) : (
          <Text>No task data available.</Text>
        )}
      </Dialog>
    </Pane>
  );
}

export default Tasks;
